import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  GetWalletsResponse,
  GetWalletByIdResponse,
  GetTransactionsResponse,
  GetTransactionsRequest,
  UpdateWalletResponse,
  UpdateWalletRequest,
  SelfWalletRequestTransfer,
  CommonResponse,
  WalletDto,
} from "./types";
import { baseQueryCreator } from "redux/baseQuery";
import env from "env";

const { walletService } = env.api.baseUrl;

const baseQuery = fetchBaseQuery({
  baseUrl: walletService,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as any).auth.accessToken;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

export const walletApi = createApi({
  reducerPath: "walletApi",
  baseQuery: baseQueryCreator(baseQuery),
  refetchOnMountOrArgChange: true,
  tagTypes: ["Wallets"],
  endpoints: (builder) => ({
    getWallets: builder.query<GetWalletsResponse, void>({
      query: () => "wallets",
      providesTags: ["Wallets"],
    }),
    getWalletById: builder.query<GetWalletByIdResponse, string>({
      query: (walletId) => `wallets/${walletId}`,
    }),
    getTransactions: builder.query<
      GetTransactionsResponse,
      GetTransactionsRequest
    >({
      query: (params) => ({
        url: "transactions",
        params: {
          ...params,
          pageSize: 99999,
        },
      }),
    }),
    createWallet: builder.mutation<{}, WalletDto>({
      query: ({ ...patch }) => ({
        url: "wallets",
        method: "POST",
        body: patch,
      }),
      invalidatesTags: ["Wallets"],
    }),
    updateWallet: builder.mutation<UpdateWalletResponse, UpdateWalletRequest>({
      query: ({ walletId, ...patch }) => ({
        url: `wallets/${walletId}`,
        method: "PATCH",
        body: patch,
      }),
    }),
    selfWalletTransfer: builder.mutation<
      CommonResponse,
      SelfWalletRequestTransfer
    >({
      query: ({ ...patch }) => ({
        url: `payments`,
        method: "POST",
        body: patch,
      }),
    })
  }),
});

export const {
  useGetWalletsQuery,
  useGetWalletByIdQuery,
  useGetTransactionsQuery,
  useUpdateWalletMutation,
  useSelfWalletTransferMutation,
  useCreateWalletMutation,
} = walletApi;
export const { useQueryState: useGetWalletByIdQueryState } =
  walletApi.endpoints.getWalletById;
export const { useLazyQuery: useGetWalletsLazyQuery } =
  walletApi.endpoints.getWallets;